import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Fee from '../fees';
import Layout from '../../components/layout/Layout';
import DocumentWrapper from '../docs/DocumentWrapper';
import TitleCheck from '../titlecheck';
import StepperHeader from '../../components/common/HorizentalLinearStepper';
import CircularIndeterminate from '../../components/common/Progress';
import SalvageDialog from '../../components/title/SalvageDialog';
import Dialog from '../../components/title/Dialog';
import TitleCheckReportDialog from '../../components/title/TitleCheckReportDialog';
import FormValidation from '../../components/utility/formValidation';
import {
  selectLocationInfo,
  selectTitleCheckLoadingState,
  selectTransactionInfo,
  selectVehicleInfo,
  selectOpenSalvageAlert,
  selectMoveToNextTab,
  selectMultiFormFeesError,
  selectDocsError,
  selectFeesError
} from '../../redux-state/selectors';
import {
  CLEAR_DOCS_ERROR,
  CLEAR_FEES_ERROR,
  CLEAR_MULTI_FORM_FEES_ERROR,
  SET_MOVE_TO_NEXT_TAB,
  SET_OPEN_SALVAGE_ALERT,
  SUBMIT_DOC,
  SUBMIT_FEES_FORM,
  SUBMIT_REFIDOC,
  SUBMIT_TITLE
} from '../../redux-state/types';
import MultiFormErrorDialog from '../../components/common/multiFormErrorDialog';
import PrimaryButton from '../../components/common/button/PrimaryButton';
import SecondaryButton from '../../components/common/button/SecondaryButton';
import { isLoggedIn } from '../../components/authProvider/AuthContext';

function MultiStepForm() {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const vehicleInfo = useSelector(selectVehicleInfo);
  const locationInfo =  useSelector(selectLocationInfo);
  const transactionInfo = useSelector(selectTransactionInfo);
  const loadingState = useSelector(selectTitleCheckLoadingState);
  const openSalvageAlert = useSelector(selectOpenSalvageAlert);
  const moveToNextTab = useSelector(selectMoveToNextTab);
  const feesErrorMultiForm = useSelector(selectMultiFormFeesError);
  const feesError = useSelector(selectFeesError);
  const docsError = useSelector(selectDocsError);
  const transactionType = transactionInfo.transactionType
  const isRefiDocs = transactionType === 'Refinance';
  const selectedCards = location.state?.selectedCards || [];
  let validationResult;
  let formData;

  const fixedOrder = ['lien-check', 'fees', 'docs'];

  const sortedCards = fixedOrder
    .map((key) => selectedCards.find((card) => card.path === key))
    .filter(Boolean);

  const steps = sortedCards.map((card) => ({
    title: card.title,
    path: card.path,
  }));
  const nextButtonLabel = (activeStep < steps.length - 1) ? `Next: ${steps[activeStep + 1].title}` : 'Finish';

  useEffect (() => {
    if(!isLoggedIn()) {
      navigate('/login')
    }
  });

  useEffect(() => {
    if (moveToNextTab) {
      if (activeStep < steps.length - 1)
        setActiveStep((prev) => prev + 1);

      if (moveToNextTab) {
        dispatch({
          type: SET_MOVE_TO_NEXT_TAB,
          requiresMoveToNextTab: false,
        })
      }
    }
  }, [moveToNextTab])

  const handleNext = () => {
    const current = steps[activeStep];
    let path;

    switch (current.path) {
      case 'fees':
        formData = { ...locationInfo, ...vehicleInfo, ...transactionInfo };
        validationResult = FormValidation('Fees', formData);

        if (validationResult) {
          setFormErrors(validationResult);
          return;
        }

        if (activeStep === steps.length - 1){
           path = '/multiStepFormOutput'
        }

        setFormErrors({});

        dispatch({
          type: SUBMIT_FEES_FORM,
          path: path,
          navigate: navigate,
        });

        if (activeStep < steps.length - 1) {
          setActiveStep((prev) => prev + 1);
        }
        return;

      case 'docs':
        formData = { ...locationInfo, ...vehicleInfo, ...transactionInfo };

        validationResult = FormValidation('Docs', formData);

        if (validationResult) {
          setFormErrors(validationResult);
          return;
        }

        setFormErrors({});

        if (activeStep === steps.length - 1){
           path = '/multiStepFormOutput'
        }

        dispatch({
          type: isRefiDocs ? SUBMIT_REFIDOC: SUBMIT_DOC,
          path: path,
          navigate: navigate,
        });

        if (activeStep < steps.length - 1) {
          setActiveStep((prev) => prev + 1);
        }
        return;

      case 'lien-check':
        validationResult = FormValidation('Title', vehicleInfo);
        if (validationResult) {
          setFormErrors(validationResult);
          return;
        }

        setFormErrors({})

        const username = localStorage.getItem('email');
        const data = {
          vin: vehicleInfo.vinCode,
          username: username,
          type: "Full_Check"
        };

        dispatch({
          type: SUBMIT_TITLE,
          payload: data,
          requiresMoveToNextTab: true
        });
        return;

      default:
        return;
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
    else {
      navigate('/dashboard')
    }
  };

  const renderStepComponent = () => {
    const current = steps[activeStep];

    switch (current?.path) {
      case 'fees':
        return <Fee formErrors={formErrors}/>;
      case 'docs':
        return <DocumentWrapper formErrors={formErrors}/>;
      case 'lien-check':
        return <TitleCheck formErrors={formErrors}/>;
      default:
        return <div>No form found</div>;
    }
  };

  const handleClose = () => {
    dispatch({
      type: SET_OPEN_SALVAGE_ALERT,
      openSalvageAlert: false,
    })
  };

  const onCancel = () => {
    dispatch({
      type: SET_OPEN_SALVAGE_ALERT,
      openSalvageAlert: false,
    })

    setOpenDialog(true);
  };

  const handleViewReport = () => {
    setOpenReportDialog(true);
  }

  const handleDialogSave = () => {
    setOpenDialog(false);
    navigate('/dashboard')
  }

  const handleDialogClose = () => {
    setOpenDialog(false);
  }

  const handleReportDialogNextStep = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }

    dispatch({
      type: SET_OPEN_SALVAGE_ALERT,
      openSalvageAlert: false,
    })
    setOpenReportDialog(false);
  }

  const handleReportDialogClose = () => {
    setOpenReportDialog(false);
  }

  const handleCloseError = () => {
    dispatch({ type: CLEAR_MULTI_FORM_FEES_ERROR });
    dispatch({ type: CLEAR_DOCS_ERROR });
    dispatch({ type: CLEAR_FEES_ERROR})
  };

  const hasDocsCard = selectedCards.some((card) => card.path === 'docs');
  const activeFeesError = hasDocsCard ? feesErrorMultiForm : feesError;

  return (
    <Layout>
      <div className="flex flex-col min-h-[800%]">
        <div className="flex justify-start bg-[default-bg-color] border-b-2 border-white">
          <div className="w-[30vw] px-6">
            <StepperHeader
              steps={steps}
              activeStep={activeStep}
            />
          </div>
        </div>

        {loadingState && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <CircularIndeterminate />
          </div>
        )}

        <SalvageDialog open={openSalvageAlert} onSave={handleReportDialogNextStep} onClose={handleClose} onCancel={onCancel} onViewReportClick={handleViewReport} nextButton={nextButtonLabel} />
        <Dialog open={openDialog} onSave={handleDialogSave} onClose={handleDialogClose} />
        <TitleCheckReportDialog open={openReportDialog} onNext={handleReportDialogNextStep} onClose={handleReportDialogClose} nextButton={nextButtonLabel} />
        {(activeFeesError || docsError) && <MultiFormErrorDialog feesError={activeFeesError} docsError={docsError} onClose={handleCloseError} />}

        <div className="w-full px-4 py-4 bg-[default-bg-color]">
          {renderStepComponent()}
        </div>

        <div className="w-full bg-white px-8 py-6 mt-auto flex justify-between">
          <SecondaryButton
            text="Back"
            onClick={handleBack}
          />

          <PrimaryButton
            text={nextButtonLabel}
            onClick={handleNext}
            disabled={loadingState}
          />
        </div>
      </div>
    </Layout>
  );
}

export default MultiStepForm;
